import { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, Code2 } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';

type HtmlBodyEditorProps = {
  value: string;
  onChange: (html: string) => void;
  /** Tailwind height for the editing surface. */
  heightClass?: string;
  /** Rendered above the canvas — e.g. a token palette. */
  toolbarExtra?: React.ReactNode;
  disabled?: boolean;
};

/**
 * Email body editor with two views of the same HTML.
 *
 * **Design** renders the body as the recipient will see it and edits it in place
 * (`designMode` on an iframe — the same approach the tender HTML template editor
 * uses). **HTML** is the raw source. Both write to the same `value`, so a change
 * made in one shows up in the other; there is no separate "preview" copy that
 * could fall behind what is actually sent.
 *
 * The iframe is deliberately NOT re-fed `value` on every keystroke — that would
 * reload the document and drop the caret on each character. It reloads only when
 * the value changes from *outside* the editor (a template being applied, or a
 * switch back from the HTML tab), tracked by `lastEmitted`.
 */
export const HtmlBodyEditor = ({
  value,
  onChange,
  heightClass = 'h-[360px]',
  toolbarExtra,
  disabled = false,
}: HtmlBodyEditorProps) => {
  const [mode, setMode] = useState<'design' | 'html'>('design');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // What this editor last pushed upward. Anything arriving in `value` that does
  // not match it came from elsewhere and has to be loaded into the canvas.
  const lastEmitted = useRef<string>(value);
  const [snapshot, setSnapshot] = useState(value);
  const [docKey, setDocKey] = useState(0);

  const reloadCanvas = useCallback((html: string) => {
    lastEmitted.current = html;
    setSnapshot(html);
    setDocKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (value !== lastEmitted.current) reloadCanvas(value);
  }, [value, reloadCanvas]);

  const switchTo = (next: 'design' | 'html') => {
    if (next === mode) return;
    // Coming back to the canvas, the source may have been edited by hand.
    if (next === 'design') reloadCanvas(value);
    setMode(next);
  };

  const handleIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    doc.designMode = disabled ? 'off' : 'on';

    const style = doc.createElement('style');
    style.setAttribute('data-editor', 'true');
    style.textContent = `
      body {
        margin: 0; padding: 18px 22px;
        font-family: -apple-system, 'Segoe UI', Arial, sans-serif;
        font-size: 14px; line-height: 1.6; color: #111827; background: #fff;
      }
      * { caret-color: #2563eb; }
      *:focus { outline: 2px solid rgba(37, 99, 235, 0.25); outline-offset: 2px; }
    `;
    doc.head.appendChild(style);

    const emit = () => {
      const html = doc.body.innerHTML;
      lastEmitted.current = html;
      onChange(html);
    };
    doc.addEventListener('input', emit);
    doc.addEventListener('blur', emit, true);
  }, [onChange, disabled]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col min-h-0">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-gray-50 flex-wrap">
        <TabButton active={mode === 'design'} onClick={() => switchTo('design')} icon={<Eye className="h-3.5 w-3.5" />}>
          Design
        </TabButton>
        <TabButton active={mode === 'html'} onClick={() => switchTo('html')} icon={<Code2 className="h-3.5 w-3.5" />}>
          HTML
        </TabButton>
        {toolbarExtra && <div className="ml-auto flex items-center gap-1 flex-wrap">{toolbarExtra}</div>}
      </div>

      {mode === 'design' ? (
        <iframe
          key={docKey}
          ref={iframeRef}
          srcDoc={snapshot}
          onLoad={handleIframeLoad}
          title="Email body"
          sandbox="allow-same-origin"
          className={cn('w-full border-0 bg-white', heightClass)}
        />
      ) : (
        <textarea
          value={value}
          onChange={(e) => {
            lastEmitted.current = e.target.value;
            onChange(e.target.value);
          }}
          disabled={disabled}
          spellCheck={false}
          className={cn(
            'w-full p-3 text-xs font-mono leading-relaxed resize-none focus:outline-none disabled:bg-gray-50',
            heightClass,
          )}
        />
      )}
    </div>
  );
};

type TabButtonProps = {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
};

const TabButton = ({ active, onClick, icon, children }: TabButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors',
      active ? 'bg-white text-gray-900 shadow-sm border border-gray-200' : 'text-gray-500 hover:text-gray-800',
    )}
  >
    {icon}{children}
  </button>
);
